import { DwMigrationStatus } from "@/apollo/types";
import { Overlay, OverlayBackdrop } from "@/components/elements/layout/Overlay";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Heading } from "@/components/elements/Typography";
import { useFlags } from "launchdarkly-react-client-sdk";
import { CircleCheckIcon, TriangleAlert } from "lucide-react";

import classNames from "@/helpers/classNames";
import { formatPercent } from "@/shared/formatters";
import { useDataWarehouseMigrationContext } from "./DataWarehouseMigrationProvider";
import {
  MigrationError,
  TableMigrationProgress,
  ViewMigrationProgress,
} from "./useCurrentMigration";

const statusMessage: Record<DwMigrationStatus, string> = {
  [DwMigrationStatus.Started]: "Migration started",
  [DwMigrationStatus.SyncingTables]: "Syncing tables in data warehouse",
  [DwMigrationStatus.SyncingViews]: "Syncing views in data warehouse",
  [DwMigrationStatus.Failed]: "Migration failed",
  [DwMigrationStatus.Completed]: "Migration complete",
  [DwMigrationStatus.SyncingModels]: "Syncing models",
  [DwMigrationStatus.ModelMigrationDone]: "Model migration done",
  [DwMigrationStatus.StoppedSyncs]: "Stopped all syncs",
  [DwMigrationStatus.UpdatedConnection]: "Updated data warehouse connection",
  [DwMigrationStatus.DwMigrationDone]: "Data warehouse migration done",
  [DwMigrationStatus.ClearedErrors]: "Cleared data warehouse related errors",
};

export const ActiveMigrationOverlay = () => {
  const { currentMigration: migrationContext } =
    useDataWarehouseMigrationContext();
  const { migration, migrationIsActive } = migrationContext.migrationState;
  const { tableMigrationProgress, viewMigrationProgress, error } =
    migrationContext;
  const { dataWarehouseMigrationEnabled } = useFlags();

  if (!dataWarehouseMigrationEnabled) return null;
  if (!migrationIsActive) {
    return null;
  }

  return (
    <Overlay className="absolute">
      <OverlayBackdrop />
      <div className="relative flex h-full w-full items-center justify-center">
        <div className="m-4 max-w-lg rounded-sm bg-white p-8 shadow-sm dark:border dark:border-gray-700 dark:bg-gray-800">
          <OverlayContent
            status={migration.status}
            tableMigrationProgress={tableMigrationProgress}
            viewMigrationProgress={viewMigrationProgress}
            error={error}
            migrationFailed={migration.failed}
          />
        </div>
      </div>
    </Overlay>
  );
};

function OverlayContent(props: {
  status: DwMigrationStatus;
  tableMigrationProgress?: TableMigrationProgress;
  viewMigrationProgress?: ViewMigrationProgress;
  error: MigrationError | null;
  migrationFailed: boolean;
}) {
  const totalCount =
    props.status === DwMigrationStatus.SyncingTables
      ? props.tableMigrationProgress?.totalTables
      : props.status === DwMigrationStatus.SyncingViews
        ? props.viewMigrationProgress?.totalViews
        : undefined;

  const currentCount =
    props.status === DwMigrationStatus.SyncingTables
      ? props.tableMigrationProgress?.currentTableNum
      : props.status === DwMigrationStatus.SyncingViews
        ? props.viewMigrationProgress?.currentViewNum
        : undefined;
  return (
    <div className="flex flex-col gap-4">
      <Heading>Data Warehouse migration in progress</Heading>
      <div className="flex items-center gap-4">
        {props.status === DwMigrationStatus.Failed ? (
          <TriangleAlert className="h-6 text-red-500" />
        ) : props.status === DwMigrationStatus.Completed ? (
          <CircleCheckIcon className="h-6 text-green-500" />
        ) : (
          <LoadingSpinner />
        )}
        <span>
          {props.migrationFailed
            ? `Error during migration.${props.error?.retrying ? " Retrying..." : ""}`
            : statusMessage[props.status]}
          {[
            DwMigrationStatus.SyncingTables,
            DwMigrationStatus.SyncingViews,
          ].includes(props.status) &&
          currentCount != null &&
          totalCount != null &&
          !props.migrationFailed
            ? ` (${currentCount}/${totalCount})`
            : ""}
        </span>
      </div>
      {props.viewMigrationProgress &&
        props.status === DwMigrationStatus.SyncingViews && (
          <div className="flex flex-col justify-center gap-4">
            <span>
              Migrating view <b>{props.viewMigrationProgress.viewId}</b> in
              dataset <b>{props.viewMigrationProgress.datasetId}</b>.
            </span>
          </div>
        )}
      {props.tableMigrationProgress &&
        props.status === DwMigrationStatus.SyncingTables && (
          <div className="flex flex-col justify-center gap-4">
            <span>
              Migrating table <b>{props.tableMigrationProgress.tableId}</b> in
              dataset <b>{props.tableMigrationProgress.datasetId}</b>.
            </span>
            <div className="flex items-center gap-2">
              <div
                className={"flex h-4 w-full overflow-clip rounded-md border"}
              >
                <div
                  style={{
                    width: props.tableMigrationProgress.percentComplete + "%",
                  }}
                  className={classNames("h-full rounded-md bg-blue-500")}
                ></div>
              </div>
              <div className="flex w-10 items-center justify-end">
                <span className="text-sm">
                  {formatPercent(
                    props.tableMigrationProgress.percentComplete / 100,
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      {props.migrationFailed && props.error?.message && (
        <span className="text-sm text-red-500">{props.error.message}</span>
      )}
      <div className="text-sm">
        Your data warehouse is currently being migrated. This may take a while.
      </div>
    </div>
  );
}
